
import React from 'react';
import { Helmet } from 'react-helmet-async';
import Layout from '@/components/Layout/Layout';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Link } from 'react-router-dom';

const AboutUsPage = () => {
  return (
    <Layout>
      <Helmet>
        <title>About Carolina Trophy Roofs | Family-Owned Roofing Contractor | Eastern NC</title>
        <meta name="description" content="Learn about Carolina Trophy Roofs, a family-owned roofing company serving Eastern NC since 2010. Meet our expert team led by founder George 'Rusty' Pugh and discover our commitment to quality and integrity." />
        <meta name="keywords" content="about Carolina Trophy Roofs, family owned roofing, Eastern NC roofing company, Rusty Pugh, roofing contractor team, professional roofers" />
        <link rel="canonical" href="https://carolinatrophyroofs.com/about" />
        
        {/* Open Graph */}
        <meta property="og:title" content="About Carolina Trophy Roofs | Family-Owned Roofing Contractor | Eastern NC" />
        <meta property="og:description" content="Learn about our family-owned roofing company serving Eastern NC since 2010. Meet our expert team and discover our commitment to quality." />
        <meta property="og:url" content="https://carolinatrophyroofs.com/about" />
        <meta property="og:type" content="website" />
        
        {/* Twitter */}
        <meta name="twitter:title" content="About Carolina Trophy Roofs | Family-Owned Roofing Contractor | Eastern NC" />
        <meta name="twitter:description" content="Learn about our family-owned roofing company serving Eastern NC since 2010. Meet our expert team and discover our commitment to quality." />
        
        {/* Schema.org About Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "About Carolina Trophy Roofs",
            "description": "Learn about Carolina Trophy Roofs, a family-owned roofing company serving Eastern North Carolina since 2010.",
            "mainEntity": {
              "@type": "RoofingContractor",
              "name": "Carolina Trophy Roofs",
              "foundingDate": "2010",
              "founder": {
                "@type": "Person",
                "name": "George 'Rusty' Pugh",
                "jobTitle": "General Manager and Founder"
              },
              "employee": [
                {
                  "@type": "Person",
                  "name": "Francisca Alvarez",
                  "jobTitle": "Executive Administrative Coordinator"
                },
                {
                  "@type": "Person", 
                  "name": "Christina Thompson",
                  "jobTitle": "Senior Project Coordinator"
                },
                {
                  "@type": "Person",
                  "name": "Austin Edwards", 
                  "jobTitle": "Senior Executive Sales Manager"
                }
              ],
              "areaServed": "Eastern North Carolina"
            }
          })}
        </script>
      </Helmet>
      {/* Hero Section with centered content and gold title */}
      <section 
        className="relative bg-gradient-to-r from-brand-charcoal-gray to-brand-charcoal-gray/80 text-white py-20 md:py-28 overflow-hidden"
        style={{
          backgroundImage: `url('/lovable-uploads/5f3827e5-0c99-43d2-80dc-04b1857cd807.png')`,
          backgroundSize: '200px 200px',
          backgroundRepeat: 'repeat',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-brand-charcoal-gray/90"></div>
        
        <div className="relative container mx-auto px-4 z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-brand-trophy-gold drop-shadow-lg">
              About Carolina Trophy Roofs
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white max-w-3xl mx-auto drop-shadow-md">
              Your trusted roofing partner in Eastern North Carolina
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Our Story Section */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-center mb-12 text-brand-charcoal-gray">Our Story</h2>
            
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
              <div>
                <h3 className="text-2xl font-bold mb-6 text-brand-trophy-gold">Rooted in Family, Built on Integrity</h3>
                <p className="text-lg mb-6 text-gray-700 leading-relaxed">
                  Carolina Trophy Roofs is more than just a roofing company—it's a local, family-owned business built from the ground up by founder and General Manager, <strong>George "Rusty" Pugh</strong>. With deep roots in North Carolina and a legacy of hands-on experience across diverse industries—including offshore oil, manufacturing, mechanics, carpentry, and roofing—Rusty brings a unique blend of grit, knowledge, and leadership to every job.
                </p>
                
                <h3 className="text-2xl font-bold mb-6 text-brand-trophy-gold">A Commitment to Craft and Character</h3>
                <p className="text-lg text-gray-700 leading-relaxed">
                  At the heart of Carolina Trophy Roofs is Rusty's unwavering commitment to integrity, professionalism, and family values. These principles shape not only how we do business, but also how we treat our customers—like neighbors, not numbers. Our work culture reflects this dedication, creating an environment where quality, honesty, and hard work thrive.
                </p>
              </div>
              
              <div className="relative">
                <div className="bg-gradient-to-br from-brand-trophy-gold/10 to-brand-safety-orange/10 rounded-2xl p-8 border border-brand-trophy-gold/20">
                  <div className="text-center">
                    <div className="w-24 h-24 bg-brand-trophy-gold rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-brand-charcoal-gray" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                      </svg>
                    </div>
                    <h4 className="text-xl font-bold text-brand-charcoal-gray mb-3">Family Values</h4>
                    <p className="text-gray-600">Built on the foundation of treating every customer like family</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Team Section */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-center mb-4 text-brand-charcoal-gray">Meet the Team Behind the Trophy</h2>
            <p className="text-xl text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Our company is powered by a local team of professionals who bring passion, precision, and purpose to their roles
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  name: "Francisca Alvarez",
                  title: "Executive Administrative Coordinator",
                  description: "Keeps operations running smoothly with meticulous attention to systems, efficiency, and team coordination.",
                  initials: "FA"
                },
                {
                  name: "Christina Thompson",
                  title: "Senior Project Coordinator",
                  description: "Leads quality assurance and ensures every client receives attentive care and top-tier results.",
                  initials: "CT"
                },
                {
                  name: "Austin Edwards",
                  title: "Senior Executive Sales Manager",
                  description: "Guides clients through product selection and planning with clarity and expertise tailored to individual needs.",
                  initials: "AE"
                },
                {
                  name: "Joshua Jones",
                  title: "Senior Product Specialist",
                  description: "A seasoned pro in storm damage assessment and material specifications, with a sharp eye for detail.",
                  initials: "JJ"
                },
                {
                  name: "Taylor Reese",
                  title: "Customer Care Coordinator (Phone)",
                  description: "The friendly voice on the line, committed to clear communication and seamless service.",
                  initials: "TR"
                },
                {
                  name: "Jack Wright",
                  title: "Customer Care Manager (Online)",
                  description: "Oversees digital inquiries and online support, ensuring fast, helpful, and courteous responses.",
                  initials: "JW"
                }
              ].map((member, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 hover:scale-105">
                  <div className="text-center mb-4">
                    <Avatar className="h-20 w-20 mx-auto mb-4 border-4 border-brand-trophy-gold/20">
                      <AvatarFallback className="bg-brand-trophy-gold text-brand-charcoal-gray font-bold text-lg">
                        {member.initials}
                      </AvatarFallback>
                    </Avatar>
                    <h3 className="font-bold text-xl mb-1 text-brand-charcoal-gray">{member.name}</h3>
                    <p className="text-brand-safety-orange font-semibold text-sm mb-3">{member.title}</p>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">{member.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Values Section */}
          <div className="bg-gradient-to-br from-brand-charcoal-gray to-gray-800 rounded-2xl p-8 md:p-12 text-white">
            <h2 className="text-4xl font-bold text-center mb-4 text-brand-trophy-gold">Our Values in Action</h2>
            <p className="text-xl text-center mb-12 text-gray-200 max-w-4xl mx-auto">
              What sets Carolina Trophy Roofs apart isn't just the quality of our work—it's the people behind it. When you choose Carolina Trophy Roofs, you're not just getting a contractor—you're partnering with a team that treats your home like their own.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center group">
                <div className="bg-brand-trophy-gold/20 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 group-hover:bg-brand-trophy-gold/30 transition-all duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-brand-trophy-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="font-bold text-2xl mb-4 text-brand-trophy-gold">Honest Business</h3>
                <p className="text-gray-200 leading-relaxed">Transparent communication, fair pricing, and trustworthy service you can count on.</p>
              </div>
              
              <div className="text-center group">
                <div className="bg-brand-trophy-gold/20 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 group-hover:bg-brand-trophy-gold/30 transition-all duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-brand-trophy-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </div>
                <h3 className="font-bold text-2xl mb-4 text-brand-trophy-gold">High Standards</h3>
                <p className="text-gray-200 leading-relaxed">Premium materials, expert craftsmanship, and attention to detail in every project.</p>
              </div>
              
              <div className="text-center group">
                <div className="bg-brand-trophy-gold/20 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 group-hover:bg-brand-trophy-gold/30 transition-all duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-brand-trophy-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="font-bold text-2xl mb-4 text-brand-trophy-gold">Heartfelt Service</h3>
                <p className="text-gray-200 leading-relaxed">Treating every customer like family with genuine care and personal attention.</p>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Bottom CTA Section with background pattern */}
      <section 
        className="relative py-16 bg-brand-charcoal-gray text-white overflow-hidden"
        style={{
          backgroundImage: `url('/lovable-uploads/5f3827e5-0c99-43d2-80dc-04b1857cd807.png')`,
          backgroundSize: '200px 200px',
          backgroundRepeat: 'repeat',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-brand-charcoal-gray/90"></div>
        
        <div className="relative container mx-auto px-4 text-center z-10">
          <h3 className="text-3xl font-bold mb-6">Ready to work with us?</h3>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Schedule your free roof inspection today.
          </p>
          <Link to="/contact">
            <button className="bg-brand-safety-orange hover:bg-brand-safety-orange/90 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 shadow-lg">
              Contact Us
            </button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default AboutUsPage;
